// =============================================================================
// MASKING TEMPLATES - PII protection and data anonymization
// =============================================================================

interface Template {
  label: string;
  script: string;
}

export const MASKING_TEMPLATES: Record<string, Template> = {
  maskEmail: {
    label: "Mask Email",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Mask email addresses (e.g., "j***@example.com")
    if doc.contains("email") {
        doc.email = mask_email(doc.email);
    }

    result(doc, attributes)
}`,
  },
  maskPhone: {
    label: "Mask Phone",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Mask phone numbers (e.g., "+1-***-***-4567")
    if doc.contains("phone") {
        doc.phone = mask_phone(doc.phone);
    }

    result(doc, attributes)
}`,
  },
  hashPII: {
    label: "Hash PII",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Hash sensitive fields for pseudonymization
    if doc.contains("first_name") {
        doc.first_name = hash_sha256(doc.first_name);
    }
    if doc.contains("last_name") {
        doc.last_name = hash_sha256(doc.last_name);
    }
    if doc.contains("ssn") {
        doc.ssn = hash_sha256(doc.ssn);
    }

    result(doc, attributes)
}`,
  },
  fullAnonymization: {
    label: "Full Anonymization",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Hash names
    if doc.contains("first_name") {
        doc.first_name = hash_sha256(doc.first_name);
    }
    if doc.contains("last_name") {
        doc.last_name = hash_sha256(doc.last_name);
    }
    if doc.contains("full_name") {
        doc.full_name = hash_sha256(doc.full_name);
    }

    // Mask contact info
    if doc.contains("email") {
        doc.email = mask_email(doc.email);
    }
    if doc.contains("phone") {
        doc.phone = mask_phone(doc.phone);
    }

    // Mask date to year only
    if doc.contains("date_of_birth") {
        doc.date_of_birth = mask_year_only(doc.date_of_birth);
    }

    // Remove highly sensitive fields entirely
    doc.remove("ssn");
    doc.remove("password_hash");

    // Add processing metadata
    doc.processed_at = timestamp_ms();

    result(doc, attributes)
}`,
  },
  recursiveMasking: {
    label: "Recursive Masking",
    script: `// Recursively mask PII in nested structures
fn mask_value(val) {
    let val_type = type_of(val);
    if val_type == "map" {
        return mask_object(val);
    } else if val_type == "array" {
        let arr = [];
        for item in val {
            arr.push(mask_value(item));
        }
        return arr;
    }
    return val;
}

fn mask_object(obj) {
    let keys = obj.keys();
    for key in keys {
        let lower_key = key.to_lower();
        let val_type = type_of(obj[key]);

        if val_type == "string" {
            if lower_key.contains("email") {
                obj[key] = mask_email(obj[key]);
            } else if lower_key.contains("phone") || lower_key.contains("mobile") {
                obj[key] = mask_phone(obj[key]);
            } else if lower_key.contains("ssn") || lower_key.contains("social") {
                obj[key] = hash_sha256(obj[key]);
            } else if lower_key.contains("birth") || lower_key.contains("dob") {
                obj[key] = mask_year_only(obj[key]);
            } else if lower_key.contains("name") && !lower_key.contains("username") {
                obj[key] = hash_sha256(obj[key]);
            }
        } else if val_type == "map" || val_type == "array" {
            obj[key] = mask_value(obj[key]);
        }
    }
    return obj;
}

fn transform(data, attributes) {
    let doc = mask_object(data);
    doc.processed_at = timestamp_ms();
    result(doc, attributes)
}`,
  },
  redactFields: {
    label: "Redact Fields",
    script: `fn transform(data, attributes) {
    let doc = data;

    // List of fields to completely remove
    let redact_fields = [
        "ssn", "social_security", "password", "password_hash",
        "credit_card", "api_key", "secret", "token"
    ];

    for field in redact_fields {
        doc.remove(field);
    }

    // Redact nested sensitive data
    if doc.contains("address") {
        doc.address.remove("street");
        doc.address.remove("zip");
        // Keep city/country for analytics
    }

    result(doc, attributes)
}`,
  },
};

// =============================================================================
// TRANSFORMATION TEMPLATES - Data manipulation and aggregation
// =============================================================================

export const TRANSFORMATION_TEMPLATES: Record<string, Template> = {
  passthrough: {
    label: "Passthrough",
    script: `fn transform(data, attributes) {
    // Pass through without changes
    result(data, attributes)
}`,
  },
  fieldMapping: {
    label: "Field Mapping",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Create new document with renamed/mapped fields
    let result_doc = #{
        user_id: doc.id,
        user_email: doc.email,
        full_name: doc.first_name + " " + doc.last_name,
        created: doc.created_at,
        is_active: doc.status == "active"
    };

    result(result_doc, attributes)
}`,
  },
  filterFields: {
    label: "Filter Fields",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Allowlist: keep only specified fields
    let allowed = ["id", "name", "email", "status", "created_at"];
    let result_doc = #{};

    for field in allowed {
        if doc.contains(field) {
            result_doc[field] = doc[field];
        }
    }

    result(result_doc, attributes)
}`,
  },
  calculateFields: {
    label: "Calculate Fields",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Add calculated fields
    if doc.contains("price") && doc.contains("quantity") {
        doc.total = doc.price * doc.quantity;
        doc.total_with_tax = doc.total * 1.1;
    }

    // Conditional field
    doc.status_label = if doc.active { "Active" } else { "Inactive" };

    // String concatenation
    if doc.contains("city") && doc.contains("country") {
        doc.location = doc.city + ", " + doc.country;
    }

    result(doc, attributes)
}`,
  },
  aggregateArray: {
    label: "Aggregate Array",
    script: `fn transform(data, attributes) {
    let doc = data;
    let items = doc.line_items;

    // Sum total
    doc.total = items.reduce(|sum, item| sum + item.amount, 0);

    // Count with condition
    doc.active_count = items.filter(|v| v.status == "active").len;

    // Calculate average
    let sum = items.reduce(|acc, item| acc + item.price, 0.0);
    doc.avg_price = if items.len > 0 { sum / items.len } else { 0 };

    // Find min/max
    doc.max_amount = items.reduce(|max, item|
        if max == () || item.amount > max { item.amount } else { max }
    , ());

    result(doc, attributes)
}`,
  },
  groupBy: {
    label: "Group By",
    script: `fn transform(data, attributes) {
    let doc = data;
    let items = doc.transactions;

    // Group by category and aggregate
    let groups = #{};
    for item in items {
        let key = item.category;
        if !groups.contains(key) {
            groups[key] = #{ count: 0, total: 0 };
        }
        groups[key].count += 1;
        groups[key].total += item.amount;
    }

    // Convert to array format
    let summary = [];
    for key in groups.keys() {
        summary.push(#{
            category: key,
            count: groups[key].count,
            total: groups[key].total,
            avg: groups[key].total / groups[key].count
        });
    }

    doc.category_summary = summary;
    result(doc, attributes)
}`,
  },
  flattenArrays: {
    label: "Flatten Arrays",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Flatten nested tags from items
    let all_tags = [];
    for item in doc.items {
        if item.contains("tags") {
            for tag in item.tags {
                all_tags.push(tag);
            }
        }
    }
    doc.all_tags = all_tags;

    // Get unique tags
    let seen = #{};
    let unique_tags = [];
    for tag in all_tags {
        if !seen.contains(tag) {
            seen[tag] = true;
            unique_tags.push(tag);
        }
    }
    doc.unique_tags = unique_tags;

    result(doc, attributes)
}`,
  },
  pivotData: {
    label: "Pivot Data",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Pivot rows to columns
    // Input: [{ month: "Jan", value: 10 }, { month: "Feb", value: 20 }]
    // Output: { Jan: 10, Feb: 20 }
    let pivoted = #{};
    for row in doc.monthly_data {
        pivoted[row.month] = row.value;
    }
    doc.by_month = pivoted;

    result(doc, attributes)
}`,
  },
  unpivotData: {
    label: "Unpivot Data",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Unpivot columns to rows
    // Input: { jan: 10, feb: 20, mar: 30 }
    // Output: [{ month: "jan", value: 10 }, ...]
    let source = doc.sales_by_month;
    let rows = [];
    for key in source.keys() {
        rows.push(#{ month: key, value: source[key] });
    }
    doc.monthly_rows = rows;

    result(doc, attributes)
}`,
  },
  lookupJoin: {
    label: "Lookup / Join",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Build lookup index from products
    let product_index = #{};
    for p in doc.products {
        product_index[p.id] = p;
    }

    // Enrich orders with product data
    doc.orders = doc.orders.map(|order| {
        let product = product_index[order.product_id];
        if product != () {
            order.product_name = product.name;
            order.unit_price = product.price;
            order.line_total = product.price * order.quantity;
        }
        order
    });

    result(doc, attributes)
}`,
  },
  enrichMetadata: {
    label: "Enrich Metadata",
    script: `fn transform(data, attributes) {
    let doc = data;

    // Add processing metadata
    doc._processed_at = timestamp_ms();
    doc._version = "1.0";
    doc._source = attributes.source ?? "unknown";

    // Add computed hash for deduplication
    if doc.contains("id") {
        doc._hash = hash_sha256(doc.id + "_" + timestamp_ms());
    }

    result(doc, attributes)
}`,
  },
  batchProcess: {
    label: "Batch Process",
    script: `fn transform(data, attributes) {
    // Process array of documents
    let docs = data;

    let results = docs.map(|doc| {
        // Transform each document
        doc.processed_at = timestamp_ms();

        // Calculate derived fields
        if doc.contains("price") && doc.contains("qty") {
            doc.total = doc.price * doc.qty;
        }

        doc
    });

    // Filter out inactive
    results = results.filter(|doc| doc.status != "deleted");

    // Sort by date
    results = results.sort_by(|a, b| b.created_at - a.created_at);

    result(results, attributes)
}`,
  },
};
